import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, makeStyles, Typography } from '@popmenu/common-ui';
import { Skeleton } from '@material-ui/lab';
import { useFeaturedDishListsData } from '../../hooks/data/useFeaturedDishListsData';
import { classNames } from '../../../utils/withStyles';
import { DishListCard } from '../DishListCard';
import { Meta } from '../Meta';

const useStyles = makeStyles(theme => ({
  cardContainer: {
    [theme.breakpoints.down('md')]: {
      flex: `0 1 calc(50% - ${theme.spacing(3)}px)`,
    },
    [theme.breakpoints.down('sm')]: {
      flex: `0 1 calc(50% - ${theme.spacing(3)}px)`,
    },
    [theme.breakpoints.down('xs')]: {
      flex: '0 1 100%',
    },
    flex: `0 1 calc(33.33% - ${theme.spacing(3)}px)`,
    marginBottom: theme.spacing(6),
  },
  cardSkeleton: {
    borderRadius: theme.spacing(1),
    height: 0,
    paddingTop: '75%',
  },
  container: {
    // Only show 1 row of skeleton cards based on screen size
    [theme.breakpoints.down('md')]: {
      '& .infinite-skeleton:nth-of-type(n+4)': {
        display: 'none',
      },
    },
    [theme.breakpoints.down('sm')]: {
      '& .skeleton:nth-of-type(n+3)': {
        display: 'none',
      },
    },
    [theme.breakpoints.down('xs')]: {
      '& .infinite-skeleton:nth-of-type(n+2)': {
        display: 'none',
      },
    },
    '& > div': {
      alignItems: 'stretch',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
  },
  noResultsContainer: {
    padding: theme.spacing(8, 4),
    textAlign: 'center',
    width: '100%',
  },
  title: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem',
      marginTop: 0,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1rem',
      fontWeight: 500,
    },
    borderBottom: `1px solid ${theme.palette.gray.light}`,
    fontSize: '2rem',
    lineHeight: 'normal',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

export const DishListIndexPage = () => {
  const classes = useStyles();
  const { data, loading } = useFeaturedDishListsData();
  const dishLists = data?.dishLists?.records ?? [];

  const CardsSkeleton = () => (
    <React.Fragment>
      {Array.from({ length: 3 }).map((_, index) => (
        <Box
          key={index}
          className={classNames(
            'skeleton',
            classes.cardContainer,
          )}
          component="div"
        >
          <Skeleton
            className={classes.cardSkeleton}
            variant="rect"
          />
        </Box>
      ))}
    </React.Fragment>
  );

  if (loading) {
    return (
      <Box className={classes.container}>
        <Typography className={classes.title} component="h1" variant="h2">
          <FormattedMessage
            id="dish_list_index_page.title"
            defaultMessage={'Best Of Lists'}
          />
        </Typography>
        <div>
          <CardsSkeleton />
        </div>
      </Box>
    );
  }
  return (
    <Box className={classes.container}>
      <Meta
        canonicalPath="/lists"
        title="Best Of Lists"
      />
      <Typography className={classes.title} component="h1" variant="h2">
        <FormattedMessage
          id="dish_list_index_page.title"
          defaultMessage={'Best Of Lists'}
        />
      </Typography>
      <div>
        {dishLists.length === 0 && (
          <Box className={classes.noResultsContainer}>
            <Typography>
              <FormattedMessage
                id="dish_list_index_page.no_results"
                defaultMessage="Sorry, Best Of Lists are currently unavailable."
              />
            </Typography>
          </Box>
        )}
        {dishLists.map(dishList => (
          <DishListCard
            key={dishList.id}
            className={classes.cardContainer}
            {...dishList}
          />
        ))}
      </div>
    </Box>
  );
};
